import { useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Package, Radar, Truck, CheckCircle2 } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

export default function DriverDashboard() {
  const { me, refresh } = useOutletContext();
  const [ordersCount, setOrdersCount] = useState(0);
  const [inventoryCount, setInventoryCount] = useState(0);
  const [gpsOn, setGpsOn] = useState(false);
  const [lastCoords, setLastCoords] = useState(null);
  const [status, setStatus] = useState(me?.rikshaw?.status || "idle");
  const watchRef = useRef(null);
  const timerRef = useRef(null);
  const pendingRef = useRef(null);

  useEffect(() => {
    loadCounts();
    return () => stopGps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCounts = async () => {
    try {
      const [o, i] = await Promise.all([
        api.get("/driver/orders"),
        api.get("/driver/inventory"),
      ]);
      setOrdersCount(o.data.count);
      setInventoryCount(i.data.count);
    } catch (e) {
      /* ignore */
    }
  };

  const pushLocation = async (coords) => {
    try {
      await api.post("/driver/location", { lat: coords.latitude, lng: coords.longitude });
      setLastCoords({ lat: coords.latitude, lng: coords.longitude, at: new Date().toISOString() });
    } catch (err) {
      if (err?.response?.status !== 429) {
        /* ignore throttle */
      }
    }
  };

  const startGps = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported on this device.");
      return;
    }
    setGpsOn(true);
    // Push a first ping immediately
    navigator.geolocation.getCurrentPosition(
      (pos) => pushLocation(pos.coords),
      (err) => toast.error(err.message || "Could not read GPS."),
      { enableHighAccuracy: true, maximumAge: 5000 }
    );
    // Watch for updates; keep latest in pending, push every 10s
    watchRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        pendingRef.current = pos.coords;
      },
      (err) => toast.error(err.message || "GPS error."),
      { enableHighAccuracy: true, maximumAge: 5000 }
    );
    timerRef.current = setInterval(() => {
      if (pendingRef.current) pushLocation(pendingRef.current);
    }, 10_000);
    toast.success("GPS live. Your rikshaw is on the map.");
  };

  const stopGps = () => {
    if (watchRef.current != null) {
      navigator.geolocation.clearWatch(watchRef.current);
      watchRef.current = null;
    }
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
    pendingRef.current = null;
    setGpsOn(false);
  };

  const changeStatus = async (s) => {
    try {
      await api.post("/driver/status", { status: s });
      setStatus(s);
      toast.success(`Rikshaw status: ${s}`);
      refresh?.();
    } catch (e) {
      toast.error("Could not change status.");
    }
  };

  return (
    <div className="space-y-6" data-testid="driver-dashboard">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <span className="label-overline text-jow-gold">Your rikshaw</span>
        <h2 className="font-display text-3xl text-jow-navy mt-1" data-testid="dd-rikshaw-code">
          {me.rikshaw?.code} · {me.rikshaw?.city}
        </h2>
        <p className="text-sm text-jow-navy/55 mt-1">Driver · {me.driver?.name}</p>
      </motion.div>

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-3">
        <Kpi label="Pending orders" value={ordersCount} Icon={Truck} testid="kpi-orders" />
        <Kpi label="Items on board" value={inventoryCount} Icon={Package} testid="kpi-inventory" />
      </div>

      {/* Status toggle */}
      <section className="bg-white border border-jow-navy/10 p-5">
        <p className="label-overline text-jow-navy">Status</p>
        <div className="mt-3 flex gap-2 flex-wrap">
          {["active", "idle", "maintenance"].map((s) => (
            <button
              key={s}
              onClick={() => changeStatus(s)}
              className={`px-4 py-2 text-xs uppercase tracking-widest border transition ${
                status === s
                  ? "bg-jow-navy text-jow-cream border-jow-navy"
                  : "bg-white text-jow-navy border-jow-navy/15 hover:border-jow-gold"
              }`}
              data-testid={`status-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
      </section>

      {/* GPS control */}
      <section className="bg-white border border-jow-navy/10 p-5">
        <div className="flex items-start gap-3 mb-4">
          <Radar size={18} className="text-jow-gold mt-1" />
          <div>
            <p className="font-display text-lg text-jow-navy">Live GPS</p>
            <p className="text-xs text-jow-navy/55">
              We broadcast your location to customers watching /find-us. Updates every 10s.
            </p>
          </div>
        </div>
        {!gpsOn ? (
          <button
            onClick={startGps}
            className="btn-primary-jow w-full"
            data-testid="gps-start"
          >
            <MapPin size={14} /> Update my GPS
          </button>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-700" data-testid="gps-live">
              <span className="inline-flex items-center gap-2">
                <CheckCircle2 size={14} /> Broadcasting live
              </span>
              {lastCoords && (
                <code className="text-xs text-green-800">
                  {lastCoords.lat.toFixed(4)}, {lastCoords.lng.toFixed(4)}
                </code>
              )}
            </div>
            <button onClick={stopGps} className="btn-secondary-jow w-full" data-testid="gps-stop">
              Stop broadcasting
            </button>
          </div>
        )}
      </section>
    </div>
  );
}

function Kpi({ label, value, Icon, testid }) {
  return (
    <div className="bg-white border border-jow-navy/10 p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} className="text-jow-gold" />
        <span className="label-overline text-jow-navy/60">{label}</span>
      </div>
      <p className="font-display text-3xl text-jow-navy" data-testid={`${testid}-value`}>
        {value}
      </p>
    </div>
  );
}
